<template>
    <div class="mode-card">
        <div class="card">
            <!-- Header -->
            <div class="card-title">
                <div class="title-icon">
                    <img v-if="icon.endsWith('.svg')" :src="icon" class="card-icon" />
                    <i v-else :class="['mdi', icon]" class="mdi-icon"></i>
                </div>
                <span class="card-label">{{ title }}</span>

                <!-- Header Lottie Animation -->
                <div class="lottie-header">
                    <LottieWeb :animationData="currentAnimationData" width="28" height="28" :autoplay="true"
                        :loop="true" :show="!!currentAnimationData" />
                </div>
            </div>

            <!-- Loader or List Content -->
            <div class="card-content-area">
                <!-- Loader Lottie Animation -->
                <div v-if="isLoading" class="lottie-loader">
                    <LottieWeb :animationData="currentAnimationData" width="180px" height="178px" :autoplay="true"
                        :loop="true" :show="isLoading" />
                </div>

                <!-- List Content -->
                <transition name="fade">
                    <div v-if="!isLoading" class="card-list">
                        <div v-for="(item, i) in sortedItems" :key="i" class="list-item"
                            :class="{ highlight: i === 0 }">
                            <span class="chip" :class="resolveColor(mode)">{{ item.line }}</span>
                            <span class="destination">{{ item.destination }}</span>
                            <span class="time">{{ minutesUntil(item.time) }} min</span>
                        </div>
                        <div v-if="sortedItems.length === 0" class="no-data">No departures</div>
                    </div>
                </transition>
            </div>
        </div>
    </div>
</template>

<script setup>
import { computed, defineProps } from 'vue'
import LottieWeb from '@/components/general/ChildComponents/LottieWeb.vue'

// Import animation data
import lottieStrain from '@/components/general/Commute/commuteAnimations/lottie_strain.json'
import lottieUbahn from '@/components/general/Commute/commuteAnimations/lottie_ubahn.json'
import lottieBus from '@/components/general/Commute/commuteAnimations/lottie_bus.json'

const props = defineProps({
    title: String,
    icon: String,
    items: Array,
    mode: String,
    isLoading: {
        type: Boolean,
        default: false,
    },
})

const sortedItems = computed(() =>
    [...props.items].sort((a, b) => new Date(a.time) - new Date(b.time))
)

const resolveColor = (mode) => {
    switch (mode) {
        case 's-bahn': return 'green'
        case 'u-bahn': return 'blue'
        case 'bus': return 'red'
        default: return 'grey'
    }
}

const minutesUntil = (isoTime) => {
    const now = new Date()
    const then = new Date(isoTime)
    return Math.max(0, Math.round((then - now) / 60000))
}

// Get animation data based on mode
const currentAnimationData = computed(() => {
    const animationDataMap = {
        's-bahn': lottieStrain,
        'u-bahn': lottieUbahn,
        'bus': lottieBus,
    }
    return animationDataMap[props.mode] || null
})
</script>

<style scoped>
.card {
    background: linear-gradient(135deg, #ffffff 0%, #f9fafb 100%);
    border-radius: 16px;
    box-shadow:
        0 4px 16px rgba(0, 0, 0, 0.06),
        0 1px 4px rgba(0, 0, 0, 0.04);
    border: 1px solid rgba(226, 232, 240, 0.5);
    padding: 10px 12px;
    display: flex;
    flex-direction: column;
    height: 220px;
    overflow: hidden;
    transition: all 0.2s ease;
    position: relative;
}

.card::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 2px;
    background: linear-gradient(90deg, #3b82f6, #06b6d4, #10b981);
    border-radius: 16px 16px 0 0;
    opacity: 0.6;
}

.card:hover {
    transform: translateY(-1px);
    box-shadow:
        0 8px 24px rgba(0, 0, 0, 0.1),
        0 4px 8px rgba(0, 0, 0, 0.06);
}

.card-title {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    margin-bottom: 6px;
}

.card-label {
    font-weight: 600;
    font-size: 16px;
    flex-grow: 1;
    white-space: nowrap;
    color: #1e293b;
}

.title-icon {
    display: flex;
    align-items: center;
    background: linear-gradient(135deg, #f1f5f9 0%, #e2e8f0 100%);
    border-radius: 8px;
    padding: 2px;
}

.card-icon,
.mdi-icon {
    width: 20px;
    height: 20px;
    margin-right: 4px;
    color: #475569;
}

.lottie-header {
    width: 28px;
    height: 28px;
    opacity: 0.9;
}

.card-content-area {
    flex: 1;
    position: relative;
    overflow: hidden;
}

/* === Loader === */
.lottie-loader {
    display: flex;
    justify-content: center;
    align-items: center;
    height: 100%;
}

/* === List === */
.card-list {
    display: flex;
    flex-direction: column;
    gap: 6px;
    overflow-y: auto;
    max-height: 100%;
    padding-right: 4px;
    scrollbar-width: thin;
    scrollbar-color: rgba(148, 163, 184, 0.3) transparent;
}

.card-list::-webkit-scrollbar {
    width: 3px;
}

.card-list::-webkit-scrollbar-track {
    background: transparent;
}

.card-list::-webkit-scrollbar-thumb {
    background: rgba(148, 163, 184, 0.3);
    border-radius: 2px;
}

.list-item {
    display: flex;
    align-items: center;
    justify-content: space-between;
    background: rgba(248, 250, 252, 0.7);
    border: 1px solid rgba(226, 232, 240, 0.4);
    border-radius: 8px;
    padding: 6px 10px;
    font-size: 14px;
    min-height: 32px;
    transition: all 0.2s ease;
}

.list-item:hover {
    background: rgba(241, 245, 249, 0.8);
    border-color: rgba(59, 130, 246, 0.3);
    transform: translateX(1px);
}

.highlight {
    background: linear-gradient(135deg, #dbeafe 0%, #bfdbfe 100%);
    border-left: 3px solid #3b82f6;
    border-color: #60a5fa;
}

.chip {
    font-size: 12px;
    font-weight: 600;
    padding: 3px 7px;
    border-radius: 6px;
    color: white;
    min-width: 30px;
    text-align: center;
    margin-right: 8px;
    white-space: nowrap;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.green {
    background: linear-gradient(135deg, #059669 0%, #047857 100%);
}

.blue {
    background: linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%);
}

.red {
    background: linear-gradient(135deg, #dc2626 0%, #b91c1c 100%);
}

.grey {
    background: linear-gradient(135deg, #6b7280 0%, #4b5563 100%);
}

.destination {
    flex-grow: 1;
    font-weight: 500;
    font-size: 14px;
    color: #374151;
    margin-right: 8px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.time {
    font-size: 13px;
    font-weight: 600;
    color: #1e293b;
    white-space: nowrap;
    min-width: 35px;
    text-align: right;
}

.no-data {
    color: #94a3b8;
    font-size: 14px;
    text-align: center;
    padding-top: 20px;
    font-weight: 500;
}

/* === Transition === */
.fade-enter-active,
.fade-leave-active {
    transition: opacity 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
    opacity: 0;
}

/* === Responsive Tweaks === */
@media (max-width: 960px) {
    .card {
        height: 200px;
        padding: 8px 10px;
    }
}

@media (max-width: 768px) {
    .card {
        height: 190px;
        padding: 6px 8px;
    }

    .card-label {
        font-size: 15px;
    }

    .chip {
        font-size: 11px;
        min-width: 26px;
        padding: 2px 6px;
    }

    .list-item {
        font-size: 13px;
        padding: 5px 8px;
        min-height: 30px;
    }

    .destination {
        font-size: 13px;
    }

    .time {
        font-size: 12px;
    }

    .lottie-header {
        width: 24px;
        height: 24px;
    }

    .card-icon,
    .mdi-icon {
        width: 18px;
        height: 18px;
    }
}

@media (max-width: 450px) {
    .card {
        height: 180px;
    }

    .card-label {
        font-size: 14px;
    }

    .chip {
        font-size: 10px;
    }

    .list-item {
        font-size: 12px;
        padding: 4px 6px;
        min-height: 28px;
    }

    .destination,
    .time {
        font-size: 12px;
    }

    .no-data {
        font-size: 12px;
    }
}
</style>